import { createSlice } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { useCallback } from 'react'

import { Message } from 'element-react'
import { tagHotjarRecording } from '../../helpers/util'
import {
    listCompanies,
    updateUser,
    deleteCompany as deleteCompanyFromRepository,
    deleteUser as deleteUserFromRepository,
    impersonateUser as impersonateUserFromRepository,
    stopImpersonation as stopImpersonationFromRepository,
} from '../../repositories/AdminRepository'
import { sendPassowrdReset } from '../../repositories/UsersRepository'
import { updateGAUserConfig } from '../../utils/sendGAAnalyticsEvent'
import { useAuthActions } from './AuthSlice'

const initialState = {
    adminSuccess: false,
    adminHasErrored: false,
    adminIsLoading: false,
    companies: [],
    currentCompanySapId: undefined,
    errors: [],
}

const adminSlice = createSlice({
    name: 'Admin',
    initialState,
    reducers: {
        resetState(state) {
            state.adminSuccess = false
            state.adminHasErrored = false
        },
        setCurrentCompanySapId(state, { payload }) {
            state.currentCompanySapId = payload
        },
        usersHasErrored(state, { payload }) {
            state.adminSuccess = false
            state.adminHasErrored = payload.hasErrored
            state.errors = [payload.message]
        },
        fetchUsersLoading(state, { payload }) {
            state.adminSuccess = false
            state.adminHasErrored = false
            state.adminIsLoading = payload
        },
        fetchCompaniesSuccess(state, { payload }) {
            state.companies = payload
        },
        deleteUserSuccess(state, { payload }) {
            state.adminSuccess = true
            state.users = payload?.users
            state.companies = payload?.company
        },
        resetPasswordSuccess(state) {
            state.adminSuccess = true
        },
    },
})

export const { actions } = adminSlice
export default adminSlice.reducer

export const useAdminActions = () => {
    const dispatch = useDispatch()
    const { handleImpersonateSuccess } = useAuthActions()

    const fetchCompanies = useCallback(() => {
        dispatch(actions.fetchUsersLoading(true))

        listCompanies()
            .then(response => {
                dispatch(actions.fetchUsersLoading(false))
                dispatch(
                    actions.fetchCompaniesSuccess(response?.data?.companies),
                )
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                dispatch(
                    actions.usersHasErrored({
                        hasErrored: true,
                        message: error?.response?.data?.message,
                    }),
                )
                dispatch(actions.resetState())
                //response.data.message;
            })
    }, [dispatch])

    const updateUserManagementStatus = userId => {
        updateUser(userId).then(() => {
            dispatch(actions.resetState())
        })
    }

    const deleteCompany = (companyId, query) => {
        deleteCompanyFromRepository({
            companyId,
            query,
        })
            .then(response => {
                dispatch(actions.deleteUserSuccess(response.data))
                dispatch(actions.resetState())
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                dispatch(
                    actions.usersHasErrored({
                        hasErrored: true,
                        message: error?.response?.data?.message,
                    }),
                )
                dispatch(actions.resetState())
            })
    }

    const deleteUser = (userId, query) => {
        deleteUserFromRepository({ userId, query })
            .then(response => {
                dispatch(actions.deleteUserSuccess(response.data))
                dispatch(actions.resetState())
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                dispatch(
                    actions.usersHasErrored({
                        hasErrored: true,
                        message: error?.response?.data?.message,
                    }),
                )
                dispatch(actions.resetState())
            })
    }

    const impersonateUser = (userId, currentRole) => {
        dispatch(actions.fetchUsersLoading(true))
        impersonateUserFromRepository(userId)
            .then(response => {
                response.data.session.impersonated = true
                response.data.session.impersonatedBy = currentRole

                //update the storage (impersonatedId comes from API session)
                var session = JSON.stringify(response.data.session)
                localStorage.setItem('session', session)

                handleImpersonateSuccess(response.data.session)
                updateGAUserConfig()

                dispatch(actions.fetchUsersLoading(false))
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                dispatch(
                    actions.usersHasErrored({
                        hasErrored: true,
                        message: error?.response?.data?.message,
                    }),
                )
                dispatch(actions.resetState())
            })
    }

    const stopImpersonation = impersonatedId => {
        return stopImpersonationFromRepository(impersonatedId)
    }

    const forgotPassword = email => {
        sendPassowrdReset(email)
            .then(response => {
                if (response.status == 200) {
                    dispatch(actions.resetPasswordSuccess())
                    dispatch(actions.resetState())
                    Message({
                        message: 'Password Reset Sent',
                        type: 'success',
                        customClass: 'el-message--success',
                        iconClass: 'el-message__icon el-icon-circle-check',
                    })
                }
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                var errMsg = error.response.data.errors.email[0]
                    ? error.response.data.errors.email[0]
                    : error.response.data.message
                dispatch(
                    actions.usersHasErrored({
                        hasErrored: true,
                        message: errMsg,
                    }),
                )
                dispatch(actions.resetState())
            })
    }

    const setCurrentCompanySapId = sapId => {
        dispatch(actions.setCurrentCompanySapId(sapId))
    }

    return {
        fetchCompanies,
        updateUserManagementStatus,
        deleteCompany,
        deleteUser,
        impersonateUser,
        stopImpersonation,
        forgotPassword,
        setCurrentCompanySapId,
    }
}
